import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRemouladeReportSnapshot } from '@/monitoring/index';
import { ValidReportTypes } from '@/utils/helperTypes';
import { throwResponseError } from '@/utils/index';

type ReportTypeProp = ValidReportTypes | null | undefined;

// Creating the own method to fetch rewards
// So we don't use throwResponseError and avoid redirecting to
// error page on rewards page
async function RewardsFetchReportById(id: string) {
  const reportResponse = await fetch(`/api/v5/reports/${id}`);

  if (reportResponse.status === 200) {
    return reportResponse.json();
  }
  if (reportResponse.status === 202) {
    throw new Error('Report not ready');
  } else {
    throw new Error('Report does not exist');
  }
}

export function useRewardsReport(reportId: string, permalink = '') {
  return useQuery({
    queryKey: ['reward_report', reportId],
    queryFn: async () => {
      return await RewardsFetchReportById(reportId);
    },

    retry: (failureCount, error: Error) => {
      if (error.message.match(/Report not ready/)) return true;
      return false;
    },
    enabled: !!permalink,
  });
}

export async function fetchReportById(id: string) {
  const reportResponse = await fetch(`/api/v5/reports/${id}`);

  if (reportResponse.status === 200) {
    return reportResponse.json();
  }
  if (reportResponse.status === 202) {
    throw new Error('Report not ready');
  } else {
    throwResponseError('Error loading report', reportResponse);
  }
}

export function useReport(
  reportId: string,
  isMonitored = false,
  reportType: ReportTypeProp = null
) {
  let noEntitiesFailureCount = 0;
  return useQuery({
    queryKey: ['report', reportId],
    queryFn: async () => {
      const data = await fetchReportById(reportId);
      if (
        reportType === 'property' &&
        (data?.meta.no_entities === null ||
          data?.meta.no_entities === undefined) &&
        noEntitiesFailureCount < 4
      ) {
        noEntitiesFailureCount += 1;

        throw new Error(
          "Continue polling because of 'no_entities' unprocessed..."
        );
      }

      return data;
    },
    retryDelay: 2000,
    retry: (failureCount: number, error: Error) => {
      // Default retry logic for errors matching 'Report not ready'
      if (error.message.match(/Report not ready/)) {
        return true;
      }

      // Custom retry logic for reportType === 'property' and no_entities === null
      if (
        reportType === 'property' &&
        error.message.indexOf('Continue polling') !== -1
      ) {
        return true;
      }

      return false;
    },
    enabled: !isMonitored,
  });
}

export function refreshReport(reportId: string) {
  const queryClient = useQueryClient();

  return queryClient.refetchQueries(['report', reportId]);
}

export function useReportOrRemouladeSnapshotHook(isMonitored: boolean) {
  return isMonitored ? useRemouladeReportSnapshot : useReport;
}

export type UseReportResult = ReturnType<typeof useReport>;
